import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet-async'
import PageLayout from 'layouts/PageLayout'
import { DesktopScreen, MobileScreen } from 'utils/screen'
import { orderToTotalUnitPrice } from 'pages/order/modules/order'
import VenueStatus from 'common/VenueStatus'
import events from 'const/analytics-events'
import { withAnalyticsHandler } from 'enhancers/withAnalytics'
import { withRouter } from 'react-router'
import get from 'lodash/get'
import Cookies from 'js-cookie'
import { getQueryParams } from 'utils/url'
import Decimal from 'decimal.js'

import Desktop from './Desktop'
import Mobile from './Mobile'
import MenuModal from './MenuModal'
import VendorFooter from './VendorFooter'
import {
  LOCAL_STORAGE_DINEIN_TABLE,
  LOCAL_STORAGE_DINEIN_TABLE_NOTIFICATION,
  COOKIE_SHOW_MODAL_ONCE,
  STATUS_CLOSED,
  STATUS_OFFLINE,
  STATUS_BUSY
} from 'pages/vendor/const'
import { isMultiVendor as isMultiVendorEnv } from 'utils/multiVendor'
import humaniseStatus from 'components.v2/VenueImage/humaniseStatus'
import { types } from 'store/modals'
import isEmpty from 'lodash/isEmpty'
import moment from 'moment-timezone'

const VendorHelmet = ({ name, phone, address, allTags = [] }) => {
  if (!name) return null
  return (
    <Helmet encodeSpecialCharacters={false}>
      <title>{name && `${name} Food Takeaway | Menu`}</title>
      <script type="application/ld+json">{`
        {
          "@context" : "http://schema.org",
          "@type" : "Restaurant",
          "name" : "${name}",
          "telephone" : "${phone || ''}",
          "address" : {
            "@type" : "PostalAddress",
            "streetAddress" : "${address ? address.address1 || '' : ''}",
            "addressLocality" : "${address ? address.suburb || '' : ''}",
            "addressRegion" : "${address ? address.state || '' : ''}",
            "postalCode" : "${address ? address.postcode || '' : ''}"
          },
          "servesCuisine" : "${Array.isArray(allTags) &&
            allTags.map(tag => tag.name).join(' • ')}"
        }
      `}</script>
    </Helmet>
  )
}

class VendorView extends React.Component {
  state = {
    isOpen: false,
    menu: {},
  }

  openModal = menu => {
    this.props.trackEvent({
      ...events['MENU_CLICK_ITEM'],
      label: String(this.props.info.id),
    })

    const { info } = this.props

    const busyUntil = get(info,'busyUntil')
    
    if (
      get(info, 'status') === STATUS_CLOSED ||
      get(info, 'status') === STATUS_OFFLINE ||
      busyUntil
    ) {
      
      const vendorTz = get(info,'vendorProperty.timezone')

      const busyDate = moment(busyUntil).tz(vendorTz,true)

      const now = moment().tz(vendorTz, true)

      const remainingMinutes = Math.floor(moment.duration(busyDate.diff(now)).asMinutes())
      
      const isBusyMode = busyUntil && busyDate.isValid() && busyDate.isAfter(now) && remainingMinutes > 0

      const messages = new Map()
        .set(
          STATUS_CLOSED,
          `Sorry, we ${humaniseStatus(
            get(info, 'status'),
            get(info, 'openingHours'),
            busyUntil,
            vendorTz
          ).replace('Opens', 'open')}!`,
        )
        .set(
          STATUS_OFFLINE,
          'Sorry, we’re temporarily unavailable. Come back another time.',
        )
        .set(
          STATUS_BUSY,
          `Sorry, we're unavailable right now, but we'll be back online in ${remainingMinutes} minutes!`,
        )

      this.props.showDisabledVenueModal(
        messages.get(isBusyMode? STATUS_BUSY : get(info, 'status')),
        'Got it',
      )
      return
    }

    this.setState({
      isOpen: true,
      menu,
    })
  }

  closeModal = () => {
    this.setState({
      isOpen: false,
      menu: {},
    })
  }

  addOrder = order => {
    const {
      info: { id, isMultiVendor, multiVendorGroupName },
    } = this.props

    this.props.trackEvent({
      ...events['MENU_CLICK_ADD_TO_ORDER'],
      label: String(id),
    })

    isMultiVendor && isMultiVendorEnv()
      ? this.props.addMultiVendorOrder(
          id,
          { ...order, multiVendorGroupName },
          { checkout: true, removeUnavailableItems: isMultiVendorEnv() },
          get(this.props, 'info'),
        )
      : this.props.addOrder(id, order, { checkout: false })
  }

  componentDidMount() {
    const qs = getQueryParams(this.props.location.search)
    if (get(this.props, 'match.params.id') && get(qs, 'table')) {
      localStorage.setItem(
        `${LOCAL_STORAGE_DINEIN_TABLE}_${get(this.props, 'match.params.id')}`,
        JSON.stringify({
          tableid: get(qs, 'table'),
          vendorid: get(this.props, 'match.params.id'),
        }),
      )

      const notifStorageName = `${LOCAL_STORAGE_DINEIN_TABLE_NOTIFICATION}_${get(
        this.props,
        'match.params.id',
      )}_${get(qs, 'table')}`
      const dineinNotif = localStorage.getItem(notifStorageName)

      if (!dineinNotif || Cookies.get(COOKIE_SHOW_MODAL_ONCE) === '0') {
        this.props.showServingToTableModal(get(qs, 'table'), notifStorageName)
        Cookies.remove(COOKIE_SHOW_MODAL_ONCE)
      }
    }
  }

  componentWillUnmount() {
    if (this.props.visibleModal === types.GENERIC) {
      this.props.hideDisabledVenueModal()
    }
  }

  componentDidUpdate(prevProps) {
    const { profile, refreshOrderParty, match } = this.props

    const { profile: oldProfile } = prevProps

    if (!isEmpty(profile) && isEmpty(oldProfile)) {
      refreshOrderParty(get(match, 'params.id'))
    }
  }

  render() {
    const { isOpen, menu } = this.state
    const {
      info,
      isLoading,
      order,
      precinct,
      combinedSelectedOrders,
      combinedSubTotalAmount,
      combinedFeesAmount,
      combinedOrdersQuantity,
      combinedDiscountAmount,
    } = this.props

    const selectedOrders = order ? order.orders : []
    const orderQuantity = selectedOrders
      .reduce((acc, curr) => acc.plus(curr.quantity), new Decimal(0))
      .toNumber()
    const orderPrice = selectedOrders
      .reduce(
        (total, currOrder) => total.plus(orderToTotalUnitPrice(currOrder)),
        new Decimal(0),
      )
      .toNumber()

    return (
      <PageLayout isLoading={isLoading}>
        <VendorHelmet {...info} />
        <DesktopScreen>
          <VenueStatus />
          <Desktop
            orderQuantity={orderQuantity}
            orderPrice={orderPrice}
            info={info}
            isLoading={isLoading}
            openModal={this.openModal}
          />
        </DesktopScreen>

        <MobileScreen>
          <Mobile
            orderQuantity={orderQuantity}
            orderPrice={orderPrice}
            info={info}
            selectedOrders={selectedOrders}
            openModal={this.openModal}
            precinct={precinct}
            combinedSelectedOrders={combinedSelectedOrders}
            combinedSubTotalAmount={combinedSubTotalAmount}
            combinedFeesAmount={combinedFeesAmount}
            combinedOrdersQuantity={combinedOrdersQuantity}
            combinedDiscountAmount={combinedDiscountAmount}
            isLoading={isLoading}
          />
        </MobileScreen>

        {!isLoading && (
          <DesktopScreen>
            <VendorFooter {...info} />
          </DesktopScreen>
        )}

        <MenuModal
          isOpen={isOpen}
          close={this.closeModal}
          action={this.addOrder}
          menu={menu}
        />
      </PageLayout>
    )
  }
}

VendorView.propTypes = {
  location: PropTypes.object,
  order: PropTypes.object,
  venueId: PropTypes.number,
  history: PropTypes.object,
  addOrder: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  info: PropTypes.object,
  actions: PropTypes.object,
}

export const BaseVendorView = VendorView
export default withAnalyticsHandler(withRouter(VendorView))
